import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  // Avviso in sviluppo per guidare la configurazione
  // REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY devono essere impostate nell'ambiente di build
  // per permettere al client di inizializzarsi correttamente
  // eslint-disable-next-line no-console
  console.warn('Supabase non configurato: imposta REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_ANON_KEY nelle variabili d\'ambiente.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export type Accommodation = {
  id: string;
  title_it: string;
  title_en: string;
  location_it: string;
  location_en: string;
  price_it: string;
  price_en: string;
  image: string;
  images: string[];
  max_guests: number;
  bedrooms: number;
  bathrooms: number;
  size: string;
  type: string;
  description_it: string;
  description_en: string;
  amenities: {it: string, en: string}[];
  latitude: number;
  longitude: number;
  terrace: boolean;
  created_at?: string;
  // Airbnb integration fields (optional depending on DB schema)
  airbnb_id?: string | null;
  airbnb_listing_id?: string | null;
  airbnb_ical_url?: string | null;
};

// Authentication helpers
export const signIn = async (email: string, password: string) => {
  return supabase.auth.signInWithPassword({ email, password });
};

export const signOut = async () => {
  return supabase.auth.signOut();
};

export const getUser = async () => {
  const { data } = await supabase.auth.getUser();
  return data.user;
};

export const getSession = async () => {
  const { data } = await supabase.auth.getSession();
  return data.session;
};

// Test function to check Supabase connection
export const testSupabaseConnection = async () => {
  try {
    console.log('Testing Supabase connection...');
    console.log('Supabase URL set:', Boolean(supabaseUrl));
    console.log('Supabase Anon Key set:', Boolean(supabaseAnonKey));

    // Test basic connection
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    console.log('Auth check:', { user: user ? 'Authenticated' : 'Anonymous', error: authError });

    // Test storage access
    const { data: buckets, error: bucketsError } = await supabase.storage.listBuckets();
    console.log('Buckets check:', { buckets, error: bucketsError });

    // Test if images bucket exists
    const { data: imagesList, error: imagesError } = await supabase
      .storage
      .from('images')
      .list('', { limit: 1 });
    console.log('Images bucket check:', { accessible: !imagesError, error: imagesError });

    return {
      connection: true,
      auth: { user, error: authError },
      storage: { buckets, error: bucketsError },
      imagesBucket: { accessible: !imagesError, error: imagesError }
    };
  } catch (error) {
    console.error('Supabase connection test failed:', error);
    return {
      connection: false,
      error
    };
  }
};

// Storage helpers
export const setupStoragePolicies = async (): Promise<boolean> => {
  try {
    console.log('Setting up storage policies for authenticated users...');
    // Note: configurare le policy via dashboard Supabase
    return true;
  } catch (error) {
    console.error('Error setting up storage policies:', error);
    return false;
  }
};

export const checkOrCreateBucket = async (): Promise<boolean> => {
  try {
    console.log('Checking if images bucket exists...');
    const { data, error } = await supabase
      .storage
      .from('images')
      .list('', { limit: 1 });

    if (error) {
      console.log('Bucket check error:', error.message);
      if (error.message.includes('not found') || error.message.includes('does not exist')) {
        console.log('Bucket does not exist, attempting to create...');
        const { data: createData, error: createError } = await supabase
          .storage
          .createBucket('images', {
            public: true,
            allowedMimeTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
            fileSizeLimit: 5242880
          });
        if (createError) {
          console.error('Error creating bucket:', createError);
          return false;
        }
        console.log('Bucket created successfully:', createData);
        return true;
      }
      return false;
    }

    console.log('Bucket exists and is accessible');
    return true;
  } catch (error) {
    console.error('Error in checkOrCreateBucket:', error);
    return false;
  }
};

export const uploadImage = async (file: File, folderName = 'accommodations'): Promise<string | null> => {
  try {
    console.log('Starting image upload process...');
    console.log('File details:', {
      name: file.name,
      size: file.size,
      type: file.type
    });

    const { data: { user } } = await supabase.auth.getUser();
    console.log('User authentication status:', user ? 'Authenticated' : 'Not authenticated');

    const bucketExists = await checkOrCreateBucket();
    if (!bucketExists) {
      console.error('Failed to access or create images bucket');
      return null;
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    const filePath = `${folderName}/${fileName}`;

    console.log('Generated file path:', filePath);

    console.log('Attempting to upload to Supabase storage...');
    const { data, error } = await supabase
      .storage
      .from('images')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    console.log('Upload response:', { data, error });

    if (error) {
      console.error('Error uploading file:', {
        message: error.message,
        error: error
      });
      return null;
    }

    if (!data) {
      console.error('No data returned from upload');
      return null;
    }

    console.log('Upload successful, getting public URL for path:', data.path);

    const { data: publicUrlData } = supabase
      .storage
      .from('images')
      .getPublicUrl(data.path);

    console.log('Public URL generated:', publicUrlData.publicUrl);

    return publicUrlData.publicUrl;
  } catch (error) {
    console.error('Error in uploadImage (catch block):', {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : 'No stack trace',
      error: error
    });
    return null;
  }
};

export const deleteImage = async (url: string): Promise<boolean> => {
  try {
    const storageUrl = (process.env.REACT_APP_SUPABASE_URL || '') + '/storage/v1/object/public/images/';
    const filePath = url.replace(storageUrl, '');

    const { error } = await supabase
      .storage
      .from('images')
      .remove([filePath]);

    if (error) {
      console.error('Error deleting file:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteImage:', error);
    return false;
  }
};

// Accommodations API
export const getAccommodations = async (): Promise<Accommodation[]> => {
  const { data, error } = await supabase
    .from('accommodations')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching accommodations:', error);
    return [];
  }

  return data || [];
};

export const getAccommodationById = async (id: string): Promise<Accommodation | null> => {
  const { data, error } = await supabase
    .from('accommodations')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching accommodation:', error);
    return null;
  }

  return data;
};

export const createAccommodation = async (accommodation: Omit<Accommodation, 'id' | 'created_at'>) => {
  const { data, error } = await supabase
    .from('accommodations')
    .insert([accommodation])
    .select();

  if (error) {
    console.error('Error creating accommodation:', error);
    return null;
  }

  return data?.[0];
};

export const updateAccommodation = async (id: string, accommodation: Partial<Accommodation>) => {
  const { data, error } = await supabase
    .from('accommodations')
    .update(accommodation)
    .eq('id', id)
    .select();

  if (error) {
    console.error('Error updating accommodation:', error);
    return null;
  }

  return data?.[0];
};

export const deleteAccommodation = async (id: string) => {
  const { error } = await supabase
    .from('accommodations')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting accommodation:', error);
    return false;
  }

  return true;
}; 